<template>
  <widget-wrapper
    :id="configItem.chartId"
    :title="chartAttr.title"
    :subTitle="chartAttr.subtitle"
    :addtxt="chartAttr.addtxt"
    :info="chartAttr.info"
    :empty="empty"
    :loading="loadingbar"
    @ctrl="ctrlClick"
    class="custom1"
  >
    <div :style="`width: 100%; height: ${customWidgetHeight}px;margin-top: 12px;`">
      <div style="position: relative; top: -50px">
        <chart-wrapper
          ref="chartWrapper"
          v-if="showChart"
          :chartOption="chartOption"
          :chartHeight="chartHeight"
          :configItem="configItem"
        />
      </div>
    </div>
  </widget-wrapper>
</template>
<script>
import ChartWrapper from './ChartWrapper.vue';
import widgetWrapper from '@/components/scpm/widgets/common/widgetWrapperScpm';
import widgetMixin from './widgetMixin';
import moment from 'moment';
import { getWidgetData } from '../serviceApi';
import { useDateTimeFormat } from '@/composables/useDateTimeFormat';

const { formatDateTime, FULL_TIME_FORMAT } = useDateTimeFormat();

export default {
  name: 'TimeSeriesGraphWidget',
  components: { widgetWrapper, ChartWrapper },
  mixins: [widgetMixin],
  computed: {
    customWidgetHeight: function () {
      if (this.isSettingMode) return 282;
      return this.widgetHeight;
    },
    // 차트 높이
    chartHeight: function () {
      return this.customWidgetHeight - 38;
    },
    // 툴팁 높이
    tooltipHeight: function () {
      return this.chartHeight - 10;
    },
    empty() {
      return this.$util.isEmpty(this.chartItems) || !this.isWidgetAuth;
    },
    // 검색 기간 날짜 차이(day)
    searchDatePeriod() {
      return this.searchDate
        ? moment(this.searchDate.queryEndDt).diff(moment(this.searchDate.queryStartDt), 'minutes')
        : 0;
    },
    // CDN 상품 여부
    isCdnResourceType() {
      return this.configItem.chartAttr.widget.options.resourceType === 'CDN';
    },
    // CDN 일경우 Line Chart Series step : true
    lineChartStep() {
      return this.isCdnResourceType ? 'end' : '';
    },
    // CDN 일경우 chart type 'category', default : 'time'
    xAxisType() {
      return this.isCdnResourceType ? 'category' : 'time';
    }
  },
  mounted() {
    this.setSearchDate();
  },
  data() {
    return {
      queryStartDt: '', // 검색 시작 날짜
      queryEndDt: '', // 검색 종료 날자
      metricDatas: [], // API metric 데이타
      chartItems: [], // 차트 정보
      chartOption: {}, // 차트 옵션
      showChart: false, // 차트 display 여부
      metricUnit: {}, // 차트 단위
      thresholdStart: null, // 위험구간 시작 value
      thresholdEnd: null, // 위험구간 종료 value,
      chartColors: [
        // 차트 color
        '#20bac2',
        '#6952db',
        '#001a5c',
        '#0ebb59',
        '#ffc14a',
        '#d7c7fe',
        '#fd9c94',
        '#fd9c94',
        '#5c0931',
        '#166440',
        '#e6e6e6',
        '#7394ff',
        '#8b21a6',
        '#d2d3d6',
        '#f56813',
        '#90dce0',
        '#b4a9ed',
        '#808dae',
        '#87ddac',
        '#ffe0a5'
      ]
    };
  },
  methods: {
    /** API Call
     * chart data make
     */
    async getData() {
      //console.log('TimeSeriesGraphWidget', 'getData this.configItem = ', this.configItem);
      this.chartOption = {};
      this.thresholdStart = null;
      this.thresholdEnd = null;

      let options = this.configItem.chartAttr.widget.options;
      if (!options.disableIndividualItem && (!options.objects || !options.objects.length)) {
        return;
      }
      let productResourceInfosConsole = this.getProductResourceInfosConsoleParams(options);

      let objectType = '';
      if (
        options.objectType &&
        Array.isArray(this.notObjectItems[options.resourceType]) &&
        !this.notObjectItems[options.resourceType].includes(options.objectType)
      ) {
        objectType = options.objectType;
      }

      let body = {
        metricDataConditionConsole: {
          metricKey: options.metricKey,
          objectType: objectType,
          productResourceInfosConsole: productResourceInfosConsole
        },
        queryStartDt: this.queryStartDt,
        queryEndDt: this.queryEndDt
      };

      if (options.isK8sObjectType && !options.disableIndividualItem) {
        body.metricDataConditionConsole.productSubName = options.objectItems.map(
          (item) => item.key || item.value
        );

        body.metricDataConditionConsole.productSubType = objectType;

        if (options.objects && options.objects.length) {
          body.metricDataConditionConsole.objectType = options.objects[0].objectType;
        }
      }

      let header = Object.assign(
        { 'X-ResourceType': options.resourceType, 'X-Cmp-ResourceAuth': 'N' },
        this.header
      );

      const apiURL =
        this.configItem.chartAttr.widget.api || '/monitoring/product/v1/console/metric-data';

      getWidgetData('post', apiURL, body, header).then((data) => {
        this.showChart = false;
        if (data instanceof Error || data.contents.length === 0) {
          this.chartItems = [];
          return;
        }

        const { contents: metricData } = data;

        const { metricUnit, fixedUnit, displayUnit } = metricData[0];
        const listMaxValues = metricData.map((e) =>
          e.perfData.reduce((acc, cur) => Math.max(acc, cur.value), -Infinity)
        );
        const isAbleToConvert = this.$util.isUnitAbleToConvert(metricUnit);
        const maxValue = Math.max(...listMaxValues);
        const { unit: highestUnit } = isAbleToConvert
          ? this.$util.convertToHigherUnit(maxValue, metricUnit)
          : this.$util.convertNumberSymbol(maxValue, metricUnit);

        const usageUnit = fixedUnit !== null ? fixedUnit : highestUnit;
        this.metricUnit = isAbleToConvert ? usageUnit : metricUnit;

        let options = this.configItem.chartAttr.widget.options;
        let _u = this.$util.getFixedUnitInfo(data.contents);
        let result = [];
        let _queryStartDt = new Date(this.queryStartDt).getTime();
        let _queryEndDt = new Date(this.queryEndDt).getTime();

        data.contents.forEach((item) => {
          let timeDiff = moment(this.searchDate.queryEndDt).diff(
            moment(this.searchDate.queryStartDt),
            'minutes'
          );

          let datas = [];
          if (item.perfData.length > 0) {
            const decimal = 2;
            datas = item.perfData.map((el) => {
              const numVal = Number(el.value);
              const { value } = isAbleToConvert
                ? this.$util.convertToSpecificUnit(numVal, metricUnit, usageUnit)
                : this.$util.toSpecificNumberSymbol(numVal, usageUnit, metricUnit);
              const roundedValue = Number(value.toFixed(decimal));
              return [el.ts, roundedValue];
            });
          } else {
            datas = [
              [_queryStartDt, null],
              [_queryEndDt, null]
            ];
          }

          let name =
            item.productName + (item.objectDisplayName ? '.' + item.objectDisplayName : '');
          result.push({ data: datas, name: name, timeDiff: timeDiff });
        });

        this.chartItems = result;

        if (options.thresholdStart && options.thresholdEnd) {
          this.thresholdStart = this.$util[_u.callFnc](options.thresholdStart, ..._u.args).value;
          this.thresholdEnd = this.$util[_u.callFnc](options.thresholdEnd, ..._u.args).value;
        }

        this.chartOption = this.buildOption(); // 차트 옵션 셋팅
        const labelUnit = usageUnit == displayUnit ? '' : usageUnit;
        const yAxisName = `(${displayUnit || metricUnit})`;
        const yAxisLabel = labelUnit ? `{value} ${labelUnit}` : `{value}`;
        this.chartOption.yAxis = {
          name: yAxisName,
          axisLabel: {
            formatter: yAxisLabel
          }
        };
        this.showChart = true;
      });
    },

    /** 검색기간 날짜 셋팅 */
    setSearchDate() {
      let dateType = 1;
      let queryStartDt, queryEndDt;

      if (this.searchDate) {
        dateType = this.searchDate.dateType;
        queryStartDt = this.searchDate.queryStartDt;
        queryEndDt = this.searchDate.queryEndDt;
      } else {
        // 위젯 등록시 날짜 default 셋팅
        queryStartDt = moment(new Date()).add(-30, 'minutes').toISOString();
        queryEndDt = moment(new Date()).toISOString();
      }

      // Global CDN 인경우 하루전 날짜 검색 (날짜 직접 입력 검색이 아닌경우만)
      if (dateType == 1 && this.isCdnResourceType) {
        queryStartDt = moment(queryStartDt).add(-1, 'days').toISOString();
        queryEndDt = moment(queryEndDt).add(-1, 'days').toISOString();
      }

      this.queryStartDt = queryStartDt;
      this.queryEndDt = queryEndDt;
    },

    /** 차트 옵션 */
    buildOption() {
      let options = this.configItem.chartAttr.widget.options;

      // 위험도 표시
      let markArea = {
        silent: false,
        itemStyle: {
          color: 'rgba(255, 173, 177, 0.8)'
        },
        data: [
          [
            {
              yAxis: Number(this.thresholdStart)
            },
            {
              yAxis: Number(this.thresholdEnd)
            }
          ]
        ]
      };

      let series = [];
      this.chartItems.forEach((item) => {
        let seriesItem = {
          name: item.name,
          data: item.data,
          markArea: markArea,
          cursor: 'default',
          silent: true
        };

        let lineStyle = {
          width: 1
        };

        // if (item.timeDiff > 30) {
        //     seriesItem.showSymbol = false;
        // }

        if (options.chartType == 'line') {
          seriesItem.type = 'line';
          seriesItem.symbol = 'circle';
          seriesItem.symbolSize = 3;
          seriesItem.step = this.lineChartStep;
          seriesItem.lineStyle = lineStyle;
        } else if (options.chartType == 'stack') {
          seriesItem.type = 'line';
          seriesItem.symbol = 'none';
          seriesItem.stack = 'total';
          seriesItem.areaStyle = {};
        } else if (options.chartType == 'bar') {
          seriesItem.type = 'bar';
          seriesItem.symbol = 'none';
          seriesItem.stack = 'total';
          seriesItem.barMaxWidth = 12; // bar;
          seriesItem.barMinWidth = 12; //bar
        } else if (options.chartType == 'scatter') {
          seriesItem.type = 'scatter';
          seriesItem.symbolSize = 5;
        }
        series.push(seriesItem);
      });

      let option = {
        grid: {
          left: '28px',
          right: '28px',
          bottom: '35px',
          containLabel: true
        },
        color: this.chartColors,
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff',
          textStyle: {
            fontSize: 12
          },
          axisPointer: {
            type: 'line',
            label: {
              formatter: this._tooltipPointerFormatter
            }
          },
          confine: false,
          className: 'toolbar-scrollbar',
          extraCssText: 'max-height:300px; overflow:auto;',
          triggerOn: 'mousemove',
          enterable: this.chartItems.length > 12,
          transitionDuration: 0.8,
          appendToBody: true,
          position: (point, params, dom, rect, size) => {
            const position = this.$util.getPositionTooltip(point, size);

            if (position[0] < 0 && size.contentSize[0] && position[1]) {
              return [-size.contentSize[0] / 2, position[1] - 10];
            }

            return position;
          }
        },
        legend: {
          left: 0,
          bottom: 0,
          type: 'scroll'
        },

        toolbox: {
          feature: {}
        },
        xAxis: {
          type: this.xAxisType,
          boundaryGap: false,
          splitLine: {
            show: true
          },
          axisTick: { show: false },
          nameGap: -11,
          axisLabel: {
            hideOverlap: true,
            fontSize: 12,
            formatter: this._xLabelFormatter
          }
        },
        yAxis: {
          name: '(' + this.metricUnit.orgUnit + ')',
          axisLabel: {
            formatter: this._yLabelFormatter
          }
        },
        series: series
      };
      if (options.chartType == 'bar') {
        option.xAxis.boundaryGap = true;
      }

      return option;
    },

    /** 차트 툴팁 포인터 라벨 formatter */
    _tooltipPointerFormatter(params) {
      let label = Number(params.value);
      if (params.axisDimension == 'x') {
        label = formatDateTime(label, FULL_TIME_FORMAT);
      } else if (params.axisDimension == 'y') {
        label = this.$filters.comma(label.toFixed(2));
      }
      return label;
    },

    /** 차트 X축 라벨 formatter */
    _xLabelFormatter(label) {
      if (this.searchDatePeriod > 1440) {
        //return '{MM}-{dd} {HH}h';
        let time = moment(Number(label)).format('MM-DD HH').split('');
        time[5] = '\n';
        return time.join('') + 'h';
      } else {
        //return '{HH}:{mm}';
        return moment(Number(label)).format('HH:mm') + ' ';
      }
    },

    /** 차트 Y축 라벨 formatter */
    _yLabelFormatter(value) {
      value = this.$filters.comma(value);
      if (this.metricUnit.unit !== this.metricUnit.orgUnit) {
        value = value + this.metricUnit.unit;
      }
      return value;
    }
  }
};
</script>

<style scoped lang="scss">
.widget-chart-bar {
  height: auto;
  margin: 0 auto;
}

.custom1 :deep .widget-body {
  padding: 0px 20px 20px 20px;
}

.custom1 :deep .echarts {
  height: initial !important;
  .echarts-inner {
    height: initial !important;
    min-height: inherit !important;
  }
}

.custom1 :deep .chart-tooltip span {
  color: #fff !important;
}
</style>
